import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Edit, Trash, Link as LinkIcon, LineChart, Archive, Loader2, Users, Globe, Lock } from "lucide-react";
import { WishlistItemUser, Section } from "@/types/wishlist";
import { FriendGroup } from "@/types/auth";
import { wishlistApi } from "@/api/wishlist";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";

interface WishlistItemProps {
  item: WishlistItemUser;
  section?: Section;
  onEdit?: (itemId: number) => void;
  onDelete?: (itemId: number) => void;
  onArchive?: (itemId: number) => void;
  hideReservationStatus?: boolean;
  isDeleting?: boolean;
  friendGroups?: FriendGroup[];
  sections?: Section[];
}

export const WishlistItem = ({
  item,
  section,
  onEdit,
  onDelete,
  onArchive,
  hideReservationStatus = false,
  isDeleting = false,
  friendGroups = [],
  sections = []
}: WishlistItemProps) => {
  const { user } = useAuth();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (item.image_filename && user?.id) {
      setImageLoading(true);
      wishlistApi.getAuthenticatedImage(user.id, item.id, item.image_filename)
        .then(blobUrl => {
          setImageUrl(blobUrl);
        })
        .catch(error => {
          setImageUrl(null);
        })
        .finally(() => {
          setImageLoading(false);
        });
    } else {
      setImageUrl(null);
    }
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [user?.id, item.id, item.image_filename]);

  const getReservationStatus = () => {
    if (hideReservationStatus) return null;

    if (!item.reserved_by) {
      return "Not Reserved";
    }
    if (item.reserved_by_name && !item.is_anonymous) {
      return `Reserved by ${item.reserved_by_name}`;
    }
    return "Reserved";
  };

  const getEffectiveGroupId = () => {
    // Section group_id takes precedence over item group_id
    return section?.group_id || item.group_id;
  };

  const getGroupInfo = () => {
    const effectiveGroupId = getEffectiveGroupId();
    if (!effectiveGroupId) return null;
    return friendGroups.find(g => g.id === effectiveGroupId);
  };

  const isInheritedFromSection = section?.group_id && section.group_id !== item.group_id;

  const reservationStatus = getReservationStatus();
  const groupInfo = getGroupInfo();
  const effectiveGroupId = getEffectiveGroupId();

  return (
    <Card className="p-4 hover:shadow-lg transition-all duration-300 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-primary/10">
      <div className="flex flex-col gap-3">
        {item.image_filename && (
          <div className="w-full aspect-video relative rounded-lg overflow-hidden">
            {imageLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading image...</span>
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name || "Wishlist item"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                <span className="text-sm text-gray-500 dark:text-gray-400">Image unavailable</span>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-between items-start gap-4">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent break-words">
            {item.name}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(item.id)}
                className="h-8 w-8 p-0 hover:bg-primary/10"
              >
                <Edit className="h-4 w-4 text-primary" />
              </Button>
            )}
            {onArchive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onArchive(item.id)}
                className="h-8 w-8 p-0 hover:bg-orange-50 dark:hover:bg-orange-950/30 text-orange-500 dark:text-orange-400"
              >
                <Archive className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(item.id)}
                disabled={isDeleting}
                className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-500 disabled:opacity-50"
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
        {item.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between mt-2">
          <div className="flex flex-col gap-1">
            {reservationStatus && (
              <span className="text-sm text-primary/80 font-medium">
                {reservationStatus}
              </span>
            )}
            {item.message && (
              <p className="text-sm italic text-gray-600 dark:text-gray-300">
                Reserver's Message: {item.message}
              </p>
            )}
            <div className="flex items-center gap-3">
              {item.price_track && (
                <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                  <LineChart className="h-3 w-3" />
                  <span>Price tracking enabled</span>
                </div>
              )}
              
              {/* Group Access Indicator */}
              <div className="flex items-center gap-1 text-xs">
                {isInheritedFromSection ? (
                  <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-2 py-1 rounded-full">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: groupInfo?.color || '#9b87f5' }}
                    />
                    <span>Inherited from section</span>
                  </div>
                ) : effectiveGroupId ? (
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: groupInfo?.color || '#9b87f5' }}
                    />
                    <Users className="h-3 w-3" />
                    <span>{groupInfo?.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                    <Globe className="h-3 w-3" />
                    <span>All Friends</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {item.purchase_link && (
            <Button
              variant="outline"
              size="sm"
              asChild
              className="flex gap-2 items-center hover:bg-primary/10 border-primary/20"
            >
              <a
                href={item.purchase_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <LinkIcon className="h-4 w-4" />
                View Product
              </a>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};